/* @flow */
import { SellingPriceInfo as SellingPriceInfoOld } from "../../src/types/articlesearch/Article";
import {
    BulkDiscountSourceLevel,
    SellingPriceDnrInfo,
    SellingPriceDnrLevel,
    PriceAndPromotionInfo,
    PriceDisplayData
} from "../../src/types/PriceAndPromotionInfo";
import {
    SellingPriceDnRInfo,
    SellingPriceDnRLevel,
    SellingPriceInfo as SellingPriceInfoEVNew
} from "../../src/types/EvaluateArticleNew";
import { sortingCriteriaBulkDiscountLevels } from "../../src/utils/SortingCriteriaListsUtil";

type GeneralizedSellingPriceInfo = SellingPriceInfoOld | SellingPriceInfoEVNew;

export function getFlashSaleCounter<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo | undefined | null
): number | undefined | null {
    if (
        sellingPriceInfo &&
        sellingPriceInfo?.appliedAdjustments &&
        sellingPriceInfo.appliedAdjustments.includes("flash") &&
        sellingPriceInfo.dnrInfo
    ) {
        return (
            Object.values(sellingPriceInfo.dnrInfo ?? {}).find(
                (element) => !!element.flashSalesCounter && element.flashSalesCounter > 0
            ) || {}
        ).flashSalesCounter;
    }
    return null;
}

export function isAdjustmentsApplied<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo | PriceDisplayData | undefined | null,
    adjustment: string
): boolean {
    if (
        sellingPriceInfo &&
        sellingPriceInfo?.appliedAdjustments &&
        sellingPriceInfo.appliedAdjustments.includes(adjustment)
    ) {
        return true;
    }
    return false;
}

export function isCip<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo | PriceDisplayData | undefined | null
): boolean {
    return isAdjustmentsApplied(sellingPriceInfo, "cip");
}

// customer individual price breaks best price, always taken
export function isCipBbp<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo | undefined | null
): boolean {
    return isAdjustmentsApplied(sellingPriceInfo, "cipbbp");
}

export function isManualPrice<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo | undefined | null
): boolean {
    return isAdjustmentsApplied(sellingPriceInfo, "manual");
}

export function isPromotion<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo | undefined | null
): boolean {
    return isAdjustmentsApplied(sellingPriceInfo, "promotion");
}

export function isDnrActive<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo,
    amountInBasket: number | undefined | null
): boolean {
    return getActiveDnrLevel(sellingPriceInfo, amountInBasket) !== null;
}

export function isDnr<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo
): boolean {
    return !!(sellingPriceInfo && sellingPriceInfo.summaryDnrInfo);
}

function stringToNumber(s: string | null | undefined): number {
    // undefined becomes NaN, as well as Number({}), so avoid that
    return Number(s) || 0;
}

export function getActiveDnrLevel<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo,
    amountInBasket: number | undefined | null
) {
    if (sellingPriceInfo && sellingPriceInfo.summaryDnrInfo) {
        const summaryDnrInfo = sellingPriceInfo.summaryDnrInfo;
        const level = getActiveLevel(summaryDnrInfo, amountInBasket);
        if (level) {
            return summaryDnrInfo?.levels?.[level];
        }
    }
    return null;
}

export function getPieceNet<SellingPriceInfo extends GeneralizedSellingPriceInfo>(sellingPriceInfo: SellingPriceInfo) {
    if (sellingPriceInfo.finalPricesInfo) {
        return sellingPriceInfo.finalPricesInfo.singleItemWithoutEmpties;
    }
    return undefined;
}

export function getPieceGross<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo
) {
    if (sellingPriceInfo.finalPricesInfo) {
        return sellingPriceInfo.finalPricesInfo.singleItemGross;
    }
    return undefined;
}

export function getNetPriceWithDiscounts<SellingPriceInfo extends GeneralizedSellingPriceInfo>(
    sellingPriceInfo: SellingPriceInfo,
    amountInBasket: number | undefined | null
): number | undefined | null {
    const level = getActiveDnrLevel(sellingPriceInfo, amountInBasket);
    if (level) {
        return stringToNumber(level.finalSingleNetPrice);
    }

    try {
        return sellingPriceInfo?.finalPricesInfo?.articleNet;
    } catch (e: unknown) {
        return undefined;
    }
}

export function getGrossPriceWithDiscounts(
    sellingPriceInfo: SellingPriceInfoOld | SellingPriceInfoEVNew,
    amountInBasket: number | undefined | null
): number | undefined | null {
    const level = getActiveDnrLevel(sellingPriceInfo, amountInBasket);
    if (level) {
        return stringToNumber(level.finalSingleGrossPrice);
    }

    try {
        return sellingPriceInfo.finalPricesInfo?.articleGross;
    } catch (e) {
        return undefined;
    }
}

export function getActiveLevel(
    dnr: SellingPriceDnrInfo | SellingPriceDnRInfo,
    amountInBasket: number | undefined | null
): string | null {
    let active = 0;
    let activeKey = null;
    if (amountInBasket) {
        const amount = amountInBasket;
        for (const [key] of Object.entries(dnr.levels ?? {})) {
            if (+key <= amount! && +key >= active) {
                active = Number.parseFloat(key);
                activeKey = key;
            }
        }
    }
    return activeKey;
}

export function getDerivedPricePromotionInfo(
    priceAndPromotionInfo: PriceAndPromotionInfo
): Array<BulkDiscountSourceLevel> {
    const summaryDnrInfoLevels: { [key: string]: SellingPriceDnrLevel } | Record<string, SellingPriceDnRLevel> =
        (priceAndPromotionInfo.summaryDnrInfo || {}).levels || {};
    const derivedDnrLevels: Array<BulkDiscountSourceLevel> = Object.entries(summaryDnrInfoLevels).map(([key, dnrLevel]) => {
        // Convert the Article Service model of SummaryDnrInfo into the models used by the shared component
        return {
            amountStart: key,
            // The following hack is due to type incompatibilities between EV and S&D
            discountPercent: dnrLevel.value ?? "", // This is the 'saved'-percentage
            finalSingleNetPrice: dnrLevel.finalSingleNetPrice,
            finalSingleGrossPrice: dnrLevel.finalSingleGrossPrice
        };
    });
    const sortedDerivedDnrLevels = [...derivedDnrLevels].sort((a, b) => {
        for (let i = 0; i < sortingCriteriaBulkDiscountLevels.sortFn.length; i++) {
            const sortFn = sortingCriteriaBulkDiscountLevels.sortFn[i];
            const result = sortFn(a) - sortFn(b);
            if (result !== 0) {
                return sortingCriteriaBulkDiscountLevels.ordering[i] === "desc" ? -result : result;
            }
        }
        return 0;
    });
    return sortedDerivedDnrLevels;
}

export function getDnrLabels(priceAndPromotionInfo: PriceAndPromotionInfo): Array<string> {
    // Retrieve compact summary of all DnRs to decide whether to show bulk-discounts-promotion or not
    const dnrLabels: Array<string> = Object.values(priceAndPromotionInfo.dnrInfo || {})
        .map((dnr: SellingPriceDnrInfo | SellingPriceDnRInfo) => dnr.labelId)
        .filter((labelId): labelId is string => labelId !== null && labelId !== undefined);
    return dnrLabels;
}

export const prohibitedDnrLabels = ["X_FOR_Y", "3_FOR_2"];
